/**
 * Utility functions for scale calibration
 * Pure functions that can be tested without Fabric.js dependencies
 */

/**
 * Calculate the pixel distance between two points
 * @param {Object} point1 - First point {x, y}
 * @param {Object} point2 - Second point {x, y}
 * @returns {number} - Distance in pixels
 */
export const calculatePixelDistance = (point1, point2) => {
  if (!point1 || !point2) return null;

  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Calculate the scale (pixels per meter)
 * @param {number} pixelDistance - Distance in pixels
 * @param {number} realDistance - Distance in meters
 * @returns {number|null} - Scale value or null if invalid
 */
export const calculateScale = (pixelDistance, realDistance) => {
  if (!pixelDistance || !realDistance || realDistance <= 0) return null;
  return pixelDistance / realDistance;
};

/**
 * Calculate the midpoint between two points
 * @param {Object} point1 - First point {x, y}
 * @param {Object} point2 - Second point {x, y}
 * @returns {Object|null} - Midpoint {x, y} or null
 */
export const calculateMidpoint = (point1, point2) => {
  if (!point1 || !point2) return null;

  return {
    x: (point1.x + point2.x) / 2,
    y: (point1.y + point2.y) / 2,
  };
};

/**
 * Update calibration state when markers are moved
 * @param {Object} point1 - First marker position {x, y}
 * @param {Object} point2 - Second marker position {x, y}
 * @param {number} realDistance - Real-world distance in meters
 * @returns {Object} - Updated calibration object
 */
export const updateCalibrationState = (point1, point2, realDistance) => {
  const pixelDistance = calculatePixelDistance(point1, point2);
  const scale = calculateScale(pixelDistance, realDistance);

  return {
    point1,
    point2,
    pixelDistance,
    realDistance,
    scale,
    isCalibrated: !!(point1 && point2 && pixelDistance && scale),
  };
};

/**
 * Format real distance for display
 * @param {number} distance - Distance in meters
 * @returns {string} - Formatted string (e.g., "5.00 m")
 */
export const formatDistance = (distance) => {
  if (distance == null || isNaN(distance)) return '0.00 m';
  return `${distance.toFixed(2)} m`;
};

/**
 * Format scale for display
 * @param {number} scale - Scale value (pixels per meter)
 * @returns {string} - Formatted string (e.g., "1:125")
 */
export const formatScale = (scale) => {
  if (!scale || scale <= 0) return '(Bitte kalibrieren)';
  return `1:${Math.round(scale)}`;
};

/**
 * Validate calibration data
 * @param {Object} calibration - Calibration object
 * @returns {Object} - Validation result {isValid, errors}
 */
export const validateCalibration = (calibration) => {
  const errors = [];

  if (!calibration) {
    errors.push('Calibration object is missing');
    return { isValid: false, errors };
  }

  if (calibration.realDistance != null && calibration.realDistance <= 0) {
    errors.push('Real distance must be greater than 0');
  }

  if (calibration.isCalibrated) {
    if (!calibration.point1) errors.push('Point 1 is missing');
    if (!calibration.point2) errors.push('Point 2 is missing');
    if (!calibration.pixelDistance) errors.push('Pixel distance is missing');
    if (!calibration.scale) errors.push('Scale is missing');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Create initial calibration state
 * @param {number} defaultRealDistance - Default real distance in meters
 * @returns {Object} - Initial calibration object
 */
export const createInitialCalibration = (defaultRealDistance = 5) => ({
  point1: null,
  point2: null,
  pixelDistance: null,
  realDistance: defaultRealDistance,
  scale: null,
  isCalibrated: false,
});

/**
 * Reset calibration to initial state
 * @param {number} realDistance - Preserve the real distance value
 * @returns {Object} - Reset calibration object
 */
export const resetCalibration = (realDistance = 5) => ({
  point1: null,
  point2: null,
  pixelDistance: null,
  realDistance,
  scale: null,
  isCalibrated: false,
});

/**
 * Canonical pixels-per-meter used by the editor's grid (1 px = 1 cm).
 * Step 3 and the editor render the background image so that 1 m on the image
 * equals this many pixels on the canvas — ensuring consistent measurements
 * regardless of canvas size.
 */
export const CANONICAL_PIXELS_PER_METER = 100;

/**
 * Calculate the original (un-rendered) image's pixels-per-meter.
 *
 * Why: `calibration.scale` is measured in canvas pixels per meter at the
 * specific image-fit scale used during calibration. When the same image is
 * rendered at a different fit scale (e.g. Step 3 vs Step 2), that ratio no
 * longer applies. The native px/m is independent of canvas size and can be
 * used to derive the correct image scale on any canvas.
 */
export const calculateNativePixelsPerMeter = (pixelDistance, realDistance, imageFitScale) => {
  if (!pixelDistance || pixelDistance <= 0) return null;
  if (!realDistance || realDistance <= 0) return null;
  if (!imageFitScale || imageFitScale <= 0) return null;
  return pixelDistance / (realDistance * imageFitScale);
};

/**
 * Image scale factor that renders 1 m on image as CANONICAL_PIXELS_PER_METER on canvas.
 * Apply as fabricImage.scaleX/scaleY to make distance measurements consistent
 * with the editor's grid (50 px = 50 cm).
 */
export const imageScaleForCanonicalDisplay = (nativePixelsPerMeter) => {
  if (!nativePixelsPerMeter || nativePixelsPerMeter <= 0) return null;
  return CANONICAL_PIXELS_PER_METER / nativePixelsPerMeter;
};
